// ProductProfile.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import {
  ShoppingCart,
  Share2,
  ChevronLeft,
  ChevronRight,
  Package,
  Truck,
  Shield,
  Eye,
} from "lucide-react";
import { BsCheckCircleFill, BsTrash3Fill } from "react-icons/bs";
import { Link, useParams } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import "./ProductProfile.scss";
import WishlistButton from "../../components/general/wishlistButton/WishlistButton";
import ProductsApiService from "../../services/ProductsApiService";
import { getLanguage } from "../../utils/Helper";
import { toast } from "react-toastify";
import ShareModal from "../../components/share_modal/ShareModal";
import { useRecentlyViewed } from "../../hooks/useRecentlyViewed";
import RecentlyViewed from "../../components/recently_viewed/RecentlyViewed";
import { trackViewItem } from "../../analytics/analytics";

const ProductProfile = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [withoutBox, setWithoutBox] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  const addToCartTimeoutRef = useRef(null);
  const sliderRef = useRef(null);
  const viewItemTrackedRef = useRef(null);
  const { addToCart, removeFromCart, isInCart } = useCart();
  const lang = getLanguage();

  // Content translations
  const content = {
    EN: {
      loading: "Loading...",
      notFound: "Product not found",
      addToCart: "Add to Cart",
      removeFromCart: "Remove from Cart",
      inCart: "In Cart",
      adding: "Adding...",
      removing: "Removing...",
      added: "✓ Added",
      removed: "✓ Removed",
      outOfStock: "Out of Stock",
      color: "Color",
      size: "Size",
      quantity: "Quantity",
      description: "Description",
      freeDelivery: "Free Delivery",
      deliveryDesc: "On orders over EGP 2000",
      easyReturns: "Easy Returns",
      returnsDesc: "14-day return policy",
      securePayment: "Secure Payment",
      paymentDesc: "100% secure",
      save: "Save",
      offerEnds: "Offer ends",
      topSeller: "TOP SELLER",
      share: "Share",
      currency: "LE",
      maxQuantity: "Maximum available quantity is",
      quantityExceeded: "Quantity cannot exceed available stock",
      withoutBox: "Without Box",
      withoutBoxAvailable: "Available without box",
      withoutBoxSave: "Save 100 LE - Without Box",
      withoutBoxNote: "is available without original box",
      available: "available",
      views: "Views",
      paymentMethods: "Payment Methods",
      cashOnDelivery: "Cash on Delivery",
    },
    AR: {
      loading: "جاري التحميل...",
      notFound: "المنتج غير موجود",
      addToCart: "أضف إلى السلة",
      removeFromCart: "إزالة من السلة",
      inCart: "في السلة",
      adding: "جاري الإضافة...",
      removing: "جاري الحذف...",
      added: "✓ تمت الإضافة",
      removed: "✓ تم الحذف",
      outOfStock: "غير متوفر",
      color: "اللون",
      size: "المقاس",
      quantity: "الكمية",
      description: "الوصف",
      freeDelivery: "توصيل مجاني",
      deliveryDesc: "للطلبات فوق ٢٠٠٠ جنيه",
      easyReturns: "إرجاع سهل",
      returnsDesc: "سياسة إرجاع ١٤ يوم",
      securePayment: "دفع آمن",
      paymentDesc: "١٠٠٪ آمن",
      save: "وفر",
      offerEnds: "تنتهي العرض",
      topSeller: "الأكثر مبيعاً",
      share: "مشاركة",
      currency: "ج.م",
      maxQuantity: "الكمية المتاحة هي",
      quantityExceeded: "لا يمكن أن تتجاوز الكمية المخزون المتاح",
      withoutBox: "بدون علبة",
      withoutBoxAvailable: "متوفر بدون علبة",
      withoutBoxSave: "وفر ١٠٠ ج.م - بدون علبة",
      withoutBoxNote: "متوفر بدون العلبة الأصلية",
      available: "متاح",
      views: "مشاهدة",
      paymentMethods: "طرق الدفع",
      cashOnDelivery: "الدفع عند الإستلام",
    },
  };

  const currentContent = content[lang] || content.EN;

  const { addProduct } = useRecentlyViewed();

  // Get maximum available quantity based on withoutBox option
  const getMaxQuantity = useCallback(() => {
    if (!selectedVariant) return 0;
    return withoutBox
      ? Math.max(
          0,
          selectedVariant.withoutBoxQuantity -
            selectedVariant.withoutBoxReservedQuantity,
        )
      : Math.max(
          0,
          selectedVariant.quantity - selectedVariant.reservedQuantity,
        );
  }, [selectedVariant, withoutBox]);

  const productInCart = selectedVariant
    ? isInCart(selectedVariant.id, withoutBox)
    : false;

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await ProductsApiService.getProductDetails(productId);
        const data = response;
        setProduct(data);
        addProduct(data); // Add to recently viewed
        if (data.variants && data.variants.length > 0) {
          setSelectedVariant(data.variants[0]);
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  // Reset quantity when variant changes
  useEffect(() => {
    if (selectedVariant) {
      const maxQty = getMaxQuantity();
      if (quantity > maxQty) {
        setQuantity(Math.min(1, maxQty));
      }
      if (
        selectedVariant.withoutBoxQuantity -
          selectedVariant.withoutBoxReservedQuantity <=
        0
      ) {
        setWithoutBox(false);
      }
    }
  }, [selectedVariant, withoutBox, quantity, getMaxQuantity]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (addToCartTimeoutRef.current) {
        clearTimeout(addToCartTimeoutRef.current);
      }
    };
  }, []);

  // Track product view for Google Analytics
  useEffect(() => {
    if (!product || !selectedVariant) return;

    if (viewItemTrackedRef.current === product.id) {
      return;
    }

    trackViewItem(product, selectedVariant, lang);

    viewItemTrackedRef.current = product.id;
  }, [product, selectedVariant, lang]);

  // Slider navigation functions
  const slideNext = useCallback(() => {
    if (!sliderRef.current || !product?.relatedProducts) return;
    const slider = sliderRef.current;
    const cardWidth = 280 + 24;
    const maxScroll = slider.scrollWidth - slider.clientWidth;
    const newPosition = Math.min(sliderPosition + cardWidth * 3, maxScroll);

    slider.scrollTo({
      left: newPosition,
      behavior: "smooth",
    });
    setSliderPosition(newPosition);
  }, [sliderPosition, product]);

  const slidePrev = useCallback(() => {
    if (!sliderRef.current) return;
    const slider = sliderRef.current;
    const cardWidth = 280 + 24;
    const newPosition = Math.max(sliderPosition - cardWidth * 3, 0);

    slider.scrollTo({
      left: newPosition,
      behavior: "smooth",
    });
    setSliderPosition(newPosition);
  }, [sliderPosition]);

  const handleCartAction = useCallback(async () => {
    if (isProcessing || !selectedVariant || !product) return;

    const maxQty = getMaxQuantity();

    if (maxQty <= 0) {
      toast.error(
        lang === "EN"
          ? "This product is currently out of stock"
          : "هذا المنتج غير متوفر حالياً",
      );
      return;
    }

    if (addToCartTimeoutRef.current) {
      clearTimeout(addToCartTimeoutRef.current);
    }

    setIsProcessing(true);
    setShowSuccess(false);

    try {
      if (productInCart) {
        // ✅ Remove from cart
        await removeFromCart(product.id, selectedVariant.id, withoutBox);

        // toast.success(
        //   lang === "EN" ? "Removed from cart!" : "تم الحذف من السلة!",
        //   {
        //     position: lang === "AR" ? "top-right" : "top-left",
        //     autoClose: 2000,
        //   },
        // );

        setIsProcessing(false);
        setShowSuccess(false);
      } else {
        // ✅ Add to cart
        let finalQuantity = quantity;
        if (quantity > maxQty) {
          finalQuantity = maxQty;
          toast.warning(`${currentContent.maxQuantity} ${maxQty}`);
        }

        await addToCart(
          product,
          selectedVariant,
          finalQuantity,
          withoutBox,
          false,
        );

        setShowSuccess(true);
        setQuantity(1);

        addToCartTimeoutRef.current = setTimeout(() => {
          setIsProcessing(false);
          setShowSuccess(false);
        }, 2000);
      }
    } catch (error) {
      console.error("Error with cart action:", error);
      toast.error(lang === "EN" ? "Something went wrong" : "حدث خطأ");
      setIsProcessing(false);
      setShowSuccess(false);
    }
  }, [
    product,
    selectedVariant,
    quantity,
    withoutBox,
    addToCart,
    removeFromCart,
    isProcessing,
    lang,
    getMaxQuantity,
    currentContent,
    productInCart,
  ]);

  const handleQuantityChange = useCallback(
    (newQuantity) => {
      const maxQty = getMaxQuantity();

      if (newQuantity < 1) {
        setQuantity(1);
        return;
      }

      if (newQuantity > maxQty) {
        setQuantity(maxQty);
        return;
      }

      setQuantity(newQuantity);
    },
    [getMaxQuantity],
  );

  const handleWithoutBoxChange = useCallback((checked) => {
    setWithoutBox(checked);
    setQuantity(1);
  }, []);

  const handleColorChange = useCallback(
    (color) => {
      if (!product) return;
      const variant = product.variants.find((v) => v.color?.id === color.id);
      if (variant) {
        setSelectedVariant(variant);
        setQuantity(1);
        setWithoutBox(false);
      }
    },
    [product],
  );

  const handleSizeChange = useCallback(
    (size) => {
      if (!product) return;
      let variant = product.variants.find(
        (v) =>
          v.size?.id === size.id && v.color?.id === selectedVariant?.color?.id,
      );

      if (!variant) {
        variant = product.variants.find((v) => v.size?.id === size.id);
      }

      if (variant) {
        setSelectedVariant(variant);
        setQuantity(1);
        setWithoutBox(false);
      }
    },
    [product, selectedVariant],
  );

  // const handleShare = useCallback(async () => {
  //   const productUrl = window.location.href;

  //   // Derive values safely inside the callback
  //   const name = product
  //     ? lang === "AR" && product.nameAr
  //       ? product.nameAr
  //       : product.nameEn
  //     : "";

  //   const price = selectedVariant
  //     ? withoutBox &&
  //       selectedVariant.withoutBoxQuantity -
  //         selectedVariant.withoutBoxReservedQuantity >
  //         0
  //       ? Math.max(0, selectedVariant.price - 100)
  //       : selectedVariant.discountPrice || selectedVariant.price || 0
  //     : 0;

  //   const currency = lang === "AR" ? "ج.م" : "LE";
  //   const shareText = `${name} — ${price?.toFixed(2)} ${currency}`;

  //   if (navigator.share) {
  //     try {
  //       await navigator.share({
  //         title: name,
  //         text: shareText,
  //         url: productUrl,
  //       });
  //     } catch {
  //       // user cancelled
  //     }
  //     return;
  //   }

  //   try {
  //     await navigator.clipboard.writeText(productUrl);
  //     toast.success(lang === "AR" ? "تم نسخ الرابط! 🔗" : "Link copied! 🔗", {
  //       position: lang === "AR" ? "top-right" : "top-left",
  //       autoClose: 2000,
  //     });
  //   } catch {
  //     const el = document.createElement("input");
  //     el.value = productUrl;
  //     document.body.appendChild(el);
  //     el.select();
  //     document.execCommand("copy");
  //     document.body.removeChild(el);
  //     toast.success(lang === "AR" ? "تم نسخ الرابط! 🔗" : "Link copied! 🔗", {
  //       autoClose: 2000,
  //     });
  //   }
  // }, [product, selectedVariant, withoutBox, lang]);

  const nextImage = useCallback(() => {
    if (!product) return;
    setSelectedImageIndex((prev) => (prev + 1) % product.images.length);
  }, [product]);

  const prevImage = useCallback(() => {
    if (!product) return;
    setSelectedImageIndex(
      (prev) => (prev - 1 + product.images.length) % product.images.length,
    );
  }, [product]);

  const handleShare = useCallback(async () => {
    if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
      try {
        await navigator.share({
          title: product?.nameEn,
          text: `${product?.nameEn} — ${selectedVariant?.discountPrice || selectedVariant?.price} LE`,
          url: window.location.href,
        });
      } catch {}
      return;
    }
    setShowShareModal(true);
  }, [product, selectedVariant]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{currentContent.loading}</span>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container py-5 text-center">
        <h2>{currentContent.notFound}</h2>
      </div>
    );
  }

  const currentPrice =
    selectedVariant?.discountPrice || selectedVariant?.price || 0;
  const originalPrice = selectedVariant?.price || 0;

  const WITHOUT_BOX_DISCOUNT = 100;
  const displayPrice =
    withoutBox &&
    selectedVariant?.withoutBoxQuantity -
      selectedVariant?.withoutBoxReservedQuantity >
      0
      ? Math.max(0, currentPrice - WITHOUT_BOX_DISCOUNT)
      : currentPrice;
  const displayOriginalPrice =
    withoutBox &&
    selectedVariant?.withoutBoxQuantity -
      selectedVariant?.withoutBoxReservedQuantity >
      0
      ? originalPrice
      : originalPrice;

  const hasDiscount = withoutBox
    ? selectedVariant?.withoutBoxQuantity -
        selectedVariant?.withoutBoxReservedQuantity >
      0
    : selectedVariant?.discountPrice &&
      selectedVariant.discountPrice < selectedVariant.price;

  const discountPercentage = hasDiscount
    ? Math.round(
        ((displayOriginalPrice - displayPrice) / displayOriginalPrice) * 100,
      )
    : 0;

  const availableColors = [
    ...new Map(product.variants.map((v) => [v.color?.id, v.color])).values(),
  ].filter(Boolean);
  const availableSizes = [
    ...new Map(product.variants.map((v) => [v.size?.id, v.size])).values(),
  ].filter(Boolean);

  const productName =
    lang === "AR" && product.nameAr ? product.nameAr : product.nameEn;
  const productDescription =
    lang === "AR" && product.descriptionAr
      ? product.descriptionAr
      : product.descriptionEn;

  const getLocalizedName = (item) => {
    if (!item) return "";
    return lang === "AR" && item.nameAr ? item.nameAr : item.nameEn;
  };

  const categoryPath = () => {
    const parts = [product.collection, product.category, product.subCategory]
      .filter(Boolean)
      .map((item) => getLocalizedName(item));

    return parts.join(" / ");
  };

  const maxQuantity = getMaxQuantity();
  const isOutOfStock = maxQuantity <= 0;
  const hasWithoutBoxOption =
    selectedVariant &&
    Math.max(
      0,
      selectedVariant.withoutBoxQuantity -
        selectedVariant.withoutBoxReservedQuantity,
    ) > 0;

  return (
    <div
      className={`product-profile min-vh-100 ${lang === "AR" ? "rtl" : "ltr"}`}
      style={{ paddingTop: "200px" }}
    >
      <div className="container">
        <div className="row g-4">
          {/* Image Gallery */}
          <div className="col-md-6">
            <div className="image-gallery">
              <div className="main-image-container position-relative">
                <img
                  src={product.images[selectedImageIndex]?.image_url}
                  alt={productName}
                  className="main-image rounded-3"
                />

                <div className="position-absolute top-0 start-0 m-3">
                  {hasDiscount && (
                    <span className="badge bg-danger badge-lg me-2">
                      -{discountPercentage}%
                    </span>
                  )}
                  {product.isTopSeller && (
                    <span className="badge bg-warning text-dark badge-lg">
                      {currentContent.topSeller}
                    </span>
                  )}
                </div>

                {product.images.length > 1 && (
                  <>
                    <button
                      className="btn btn-light btn-sm position-absolute top-50 start-0 translate-middle-y ms-2 rounded-circle"
                      onClick={prevImage}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <button
                      className="btn btn-light btn-sm position-absolute top-50 end-0 translate-middle-y me-2 rounded-circle"
                      onClick={nextImage}
                    >
                      <ChevronRight size={20} />
                    </button>
                  </>
                )}
              </div>

              {product.images.length > 1 && (
                <div className="thumbnails d-flex gap-2 mt-3">
                  {product.images.map((img, index) => (
                    <div
                      key={img.id}
                      className={`thumbnail ${selectedImageIndex === index ? "active" : ""}`}
                      onClick={() => setSelectedImageIndex(index)}
                    >
                      <img
                        src={img.image_url}
                        alt={`${productName} ${index + 1}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Product Details */}
          <div className="col-md-6">
            <div className="product-profile-details">
              {/* Price */}
              <div className="price-section mb-1">
                <div className="d-flex align-items-center justify-content-center gap-3">
                  <h2 className="current-price mb-0">
                    {displayPrice?.toFixed(2)} {currentContent.currency}
                  </h2>
                  {hasDiscount && (
                    <>
                      <h4 className="original-price mb-0 text-decoration-line-through text-muted">
                        {displayOriginalPrice?.toFixed(2)}{" "}
                        {currentContent.currency}
                      </h4>
                      <span className="badge bg-success">
                        {currentContent.save}{" "}
                        {(displayOriginalPrice - displayPrice)?.toFixed(2)}{" "}
                        {currentContent.currency}
                      </span>
                    </>
                  )}
                </div>
                {withoutBox && hasWithoutBoxOption && (
                  <div className="text-center mt-2">
                    <span className="badge bg-info text-white">
                      {currentContent.withoutBoxSave}
                    </span>
                  </div>
                )}
              </div>

              {/* Brand */}
              {product.brand && (
                <div className="brand-section mb-3">
                  <img
                    src={product.brand.imageUrl}
                    alt={getLocalizedName(product.brand)}
                    className="brand-image"
                  />
                </div>
              )}

              {/* Title */}
              <h1 className="product-profile-title mb-2">{productName}</h1>

              {/* Views Counter */}
              <div className="views-counter mb-3">
                <Eye size={18} />
                <span>
                  {product.viewed} {currentContent.views}
                </span>
              </div>

              {/* Category */}
              <p className="text-muted mb-3">{categoryPath()}</p>

              {/* Color Selection */}
              {availableColors.length > 0 && (
                <div className="variant-section my-4">
                  <h6 className="mb-3">
                    {currentContent.color}:{" "}
                    <span className="fw-normal">
                      {getLocalizedName(selectedVariant?.color)}
                    </span>
                  </h6>
                  <div className="d-flex gap-2 flex-wrap">
                    {availableColors.map((color) => (
                      <div
                        key={color.id}
                        className={`color-selector ${selectedVariant?.color?.id === color.id ? "selected" : ""}`}
                        style={{ backgroundColor: color.hexCode }}
                        onClick={() => handleColorChange(color)}
                        title={getLocalizedName(color)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Size Selection */}
              {availableSizes.length > 0 && (
                <div className="variant-section mb-4">
                  <h6 className="mb-3">{currentContent.size}</h6>
                  <div className="d-flex gap-2 flex-wrap">
                    {availableSizes.map((size) => (
                      <button
                        key={size.id}
                        className={`btn size-selector ${selectedVariant?.size?.id === size.id ? "selected" : ""}`}
                        onClick={() => handleSizeChange(size)}
                      >
                        {getLocalizedName(size)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Without Box Option */}
              {hasWithoutBoxOption && (
                <div className="without-box-section mb-4">
                  <div
                    className="alert alert-info d-flex align-items-center justify-content-between p-3"
                    role="alert"
                  >
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="checkbox"
                        className="form-check-input m-0"
                        id="withoutBoxCheck"
                        checked={withoutBox}
                        onChange={(e) =>
                          handleWithoutBoxChange(e.target.checked)
                        }
                        style={{
                          cursor: "pointer",
                          width: "20px",
                          height: "20px",
                        }}
                      />
                      <label
                        htmlFor="withoutBoxCheck"
                        className="mb-0 fw-semibold"
                        style={{ cursor: "pointer" }}
                      >
                        {currentContent.withoutBox}
                      </label>
                    </div>
                    <div className="text-end">
                      <small className="text-muted d-block">
                        {Math.max(
                          0,
                          selectedVariant.withoutBoxQuantity -
                            selectedVariant.withoutBoxReservedQuantity,
                        )}{" "}
                        {currentContent.available}
                      </small>
                      <small className="text-success fw-bold">
                        {currentContent.save} 100 {currentContent.currency}
                      </small>
                    </div>
                  </div>
                  <small className="text-muted d-block mt-2">
                    <i className="bi bi-info-circle me-1"></i>
                    {currentContent.withoutBoxNote}
                  </small>
                </div>
              )}

              {/* Quantity */}
              <div className="quantity-section mb-4">
                <h6 className="mb-3">{currentContent.quantity}</h6>
                <div className="d-flex align-items-center justify-content-start gap-3">
                  <div className="quantity-controls d-flex align-items-center border rounded">
                    <button
                      className="btn btn-sm"
                      onClick={() => handleQuantityChange(quantity - 1)}
                      disabled={isOutOfStock || isProcessing || quantity <= 1}
                    >
                      -
                    </button>
                    <input
                      type="text"
                      className="form-control text-center border-0 text-warning"
                      style={{ width: "60px" }}
                      value={quantity}
                      onChange={(e) =>
                        handleQuantityChange(parseInt(e.target.value) || 1)
                      }
                      onBlur={(e) => {
                        const val = parseInt(e.target.value) || 1;
                        handleQuantityChange(val);
                      }}
                      min="0"
                      max={maxQuantity}
                      disabled={isOutOfStock || isProcessing}
                    />
                    <button
                      className="btn btn-sm"
                      onClick={() => handleQuantityChange(quantity + 1)}
                      disabled={
                        isOutOfStock || isProcessing || quantity >= maxQuantity
                      }
                    >
                      +
                    </button>
                  </div>
                  {isOutOfStock ? (
                    <p className="text-danger mb-0 fw-bold">
                      {currentContent.outOfStock}
                    </p>
                  ) : (
                    <p className="text-muted mb-0 small">
                      {lang === "EN"
                        ? `${maxQuantity} available`
                        : `${maxQuantity} متاح`}
                    </p>
                  )}
                </div>
              </div>

              {/* Description Section */}
              <div className="row my-3">
                <div className="col-12">
                  <div className="description-card">
                    <div className="p-4">
                      <h4
                        className={`mb-4 ${lang === "EN" ? "text-start" : "text-end"}`}
                      >
                        {lang === "EN"
                          ? `${currentContent.description}:`
                          : `:${currentContent.description}`}
                      </h4>
                      <p className="text-muted">{productDescription}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ✅ IMPROVED: Action Buttons with Dynamic Cart State */}
              <div className="action-buttons d-flex gap-3 mb-4">
                <button
                  className={`btn-cart-action ${
                    isOutOfStock ? "out-of-stock" : ""
                  } ${isProcessing ? "loading" : ""} ${
                    showSuccess ? "success" : ""
                  } ${productInCart && !isHovered ? "in-cart" : ""} ${
                    productInCart && isHovered ? "remove-mode" : ""
                  }`}
                  onClick={handleCartAction}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  disabled={isOutOfStock || isProcessing}
                  title={
                    isOutOfStock
                      ? currentContent.outOfStock
                      : productInCart && isHovered
                        ? currentContent.removeFromCart
                        : productInCart
                          ? currentContent.inCart
                          : currentContent.addToCart
                  }
                >
                  {/* Icon Container */}
                  <div className="button-icon">
                    {showSuccess ? (
                      <BsCheckCircleFill size={20} className="icon-check" />
                    ) : productInCart && isHovered ? (
                      <BsTrash3Fill size={20} className="icon-trash" />
                    ) : (
                      <ShoppingCart size={20} className="icon-bag" />
                    )}
                  </div>

                  {/* Text */}
                  <span className="button-text">
                    {showSuccess
                      ? currentContent.added
                      : isProcessing
                        ? productInCart
                          ? currentContent.removing
                          : currentContent.adding
                        : productInCart && isHovered
                          ? currentContent.removeFromCart
                          : productInCart
                            ? currentContent.inCart
                            : currentContent.addToCart}
                  </span>

                  {/* Check Badge */}
                  {productInCart && !isHovered && !showSuccess && (
                    <div className="in-cart-badge">
                      <BsCheckCircleFill size={16} />
                    </div>
                  )}

                  {/* Loading Spinner */}
                  {isProcessing && <div className="loading-spinner" />}
                </button>

                {/* Wishlist Button */}
                {selectedVariant && (
                  <WishlistButton
                    productVariantId={selectedVariant.id}
                    className="large"
                  />
                )}

                <button
                  className="btn btn-outline-secondary btn-lg share-btn"
                  onClick={handleShare}
                  title={lang === "AR" ? "مشاركة المنتج" : "Share product"}
                >
                  <Share2 size={20} />
                </button>
                <ShareModal
                  isOpen={showShareModal}
                  onClose={() => setShowShareModal(false)}
                  url={window.location.href}
                  title={productName}
                  price={`${displayPrice?.toFixed(2)}`}
                  currency={currentContent.currency}
                />
              </div>

              {/* Payment Methods */}
              <div className="payment-methods-section mt-4">
                <h6 className="payment-methods-title mb-3">
                  {currentContent.paymentMethods}
                </h6>
                <div className="payment-methods-grid">
                  <img
                    src="/assets/payment-methods.png"
                    alt="payment-method"
                    width={"100%"}
                  />
                  {/* <img
                    src="/assets/payment-method-instapay.png"
                    alt="payment-method"
                    width={"100%"}
                  /> */}
                  {/* <img
                    src="/assets/payment-method-visa.png"
                    alt="payment-method"
                    width={"100%"}
                  />
                  <img
                    src="/assets/payment-method-wallet.png"
                    alt="payment-method"
                    width={"100%"}
                  /> */}
                </div>
              </div>

              {/* Features */}
              <div className="features-section">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="feature-card text-center p-3">
                      <Truck size={32} className="mb-2 text-warning" />
                      <p className="small mb-0 fw-semibold">
                        {currentContent.freeDelivery}
                      </p>
                      <p className="small text-muted mb-0">
                        {currentContent.deliveryDesc}
                      </p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="feature-card text-center p-3">
                      <Package size={32} className="mb-2 text-warning" />
                      <p className="small mb-0 fw-semibold">
                        {currentContent.easyReturns}
                      </p>
                      <p className="small text-muted mb-0">
                        {currentContent.returnsDesc}
                      </p>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="feature-card text-center p-3">
                      <Shield size={32} className="mb-2 text-warning" />
                      <p className="small mb-0 fw-semibold">
                        {currentContent.securePayment}
                      </p>
                      <p className="small text-muted mb-0">
                        {currentContent.paymentDesc}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Related Products Slider */}
      {product.relatedProducts && product.relatedProducts.length > 0 && (
        <div className="related-products-section mt-5">
          <div className="container">
            <div className="section-header mb-4">
              <h2 className="section-title">
                {lang === "AR" ? "منتجات ذات صلة" : "Related Products"}
              </h2>
              <div className="title-underline"></div>
            </div>

            <div className="related-products-slider-wrapper position-relative">
              <button
                className="slider-nav-btn slider-nav-prev"
                onClick={slidePrev}
                disabled={sliderPosition === 0}
              >
                <ChevronLeft size={24} />
              </button>

              <div className="related-products-slider" ref={sliderRef}>
                <div className="products-track">
                  {product.relatedProducts.map((relatedProduct) => {
                    const productName =
                      lang === "AR" && relatedProduct.nameAr
                        ? relatedProduct.nameAr
                        : relatedProduct.nameEn;
                    const brandName =
                      lang === "AR" && relatedProduct.brand?.name_ar
                        ? relatedProduct.brand.name_ar
                        : relatedProduct.brand?.name_en;

                    const price = relatedProduct.price;
                    const discountPrice = relatedProduct.discountPrice;
                    const displayPrice = discountPrice ? discountPrice : price;
                    const hasDiscount = discountPrice && discountPrice < price;
                    const discountPercent = hasDiscount
                      ? Math.round(((price - discountPrice) / price) * 100)
                      : 0;

                    const collectionName = product?.collection?.nameEn
                      .toString()
                      .toLowerCase()
                      .trim()
                      .split(" ")
                      .join("-");
                    const catName = product?.category?.nameEn
                      .toString()
                      .toLowerCase()
                      .trim()
                      .split(" ")
                      .join("-");
                    const subCatName = product?.subCategory?.nameEn
                      .toString()
                      .toLowerCase()
                      .trim()
                      .split(" ")
                      .join("-");

                    const productUrl = !subCatName
                      ? `/collection/${collectionName}/${catName}/${relatedProduct.id}`
                      : `/collection/${collectionName}/${catName}/${subCatName}/${relatedProduct.id}`;

                    return (
                      <div key={relatedProduct.id} className="product-slide">
                        <Link
                          to={productUrl}
                          className="text-decoration-none text-reset"
                        >
                          <div className="product-card">
                            <div className="product-image-wrapper">
                              <img
                                src={relatedProduct.imageUrl}
                                alt={productName}
                                className="product-image"
                              />

                              <div className="product-badges">
                                {hasDiscount && (
                                  <span className="badge badge-discount">
                                    -{discountPercent}%
                                  </span>
                                )}
                                {relatedProduct.isTopSeller && (
                                  <span className="badge badge-topseller">
                                    {lang === "AR"
                                      ? "الأكثر مبيعاً"
                                      : "TOP SELLER"}
                                  </span>
                                )}
                                {relatedProduct.isTrend && (
                                  <span className="badge badge-trend">
                                    {lang === "AR" ? "ترند" : "TRENDING"}
                                  </span>
                                )}
                                {relatedProduct.isNew && (
                                  <span className="badge badge-new">
                                    {lang === "AR" ? "جديد" : "NEW"}
                                  </span>
                                )}
                              </div>

                              <div className="quick-view-overlay">
                                <span className="quick-view-text">
                                  {lang === "AR" ? "عرض سريع" : "Quick View"}
                                </span>
                              </div>
                            </div>

                            <div className="product-info">
                              {brandName && (
                                <p className="product-brand">{brandName}</p>
                              )}
                              <h3 className="product-name">{productName}</h3>

                              <div className="product-price">
                                <span className="current-price">
                                  {displayPrice?.toFixed(2)}{" "}
                                  {currentContent.currency}
                                </span>
                                {hasDiscount && (
                                  <span className="original-price">
                                    {price?.toFixed(2)}{" "}
                                    {currentContent.currency}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </div>

              <button
                className="slider-nav-btn slider-nav-next"
                onClick={slideNext}
              >
                <ChevronRight size={24} />
              </button>
            </div>
          </div>
        </div>
      )}

      <RecentlyViewed excludeId={Number(productId)} />
    </div>
  );
};

export default ProductProfile;
